import { Head, Link } from "@inertiajs/react"
import {
  ArrowDownToLine,
  BarChart3,
  FileText,
  Grid2x2,
  LayoutList,
  DollarSign,
} from "lucide-react"
import { Logo } from "@/components/ui/logo"
import { Button } from "@/components/ui/button"

const DESCRIPTION =
  "FloorCore Measure turns a builder's PDF plan set into traced takeoffs, priced bids, and order worksheets — one system from plan sheet to purchase order."

// The audience toggle in the design file has two settings; this is the
// "Internal (invite only)" one, which is what Measure actually is today —
// no pricing page, no self-serve signup, access by admin invite.
const FEATURES = [
  {
    icon: ArrowDownToLine,
    title: "PDF plan sets",
    body: "Upload the builder's plan set; pages process into deep-zoom sheets. Pick, label, and rotate the ones worth measuring.",
  },
  {
    icon: BarChart3,
    title: "Scale and trace",
    body: "Set scale off a known dimension, then trace rooms with snapping. Areas, perimeters, and transitions total as you draw.",
  },
  {
    icon: Grid2x2,
    title: "Seams and cut sheets",
    body: "Lay roll goods with real seam placement and get a numbered cut sheet per product — no more guessing at the warehouse.",
  },
  {
    icon: LayoutList,
    title: "Plans, lots, communities",
    body: "Draw a house model once, copy it to every lot. One bid per community, levels priced off the master takeoff.",
  },
  {
    icon: DollarSign,
    title: "Bids with live margin",
    body: "Order templates roll product plus labor into $/SF. Type a target margin and the price comes back — or the reverse.",
  },
  {
    icon: FileText,
    title: "Printable packet",
    body: "Material schedule, cut sheet, order worksheet, and a marked-up plan — the same numbers, ready for the crew and the PO.",
  },
]

const STEPS = [
  {
    n: "01",
    title: "Upload the plan set",
    body: "Drop the builder PDF; sheets render in the background.",
  },
  {
    n: "02",
    title: "Trace the takeoff",
    body: "Set scale, draw rooms, assign products and add-ons.",
  },
  {
    n: "03",
    title: "Price the bid",
    body: "Levels pull quantities; templates supply the cost per SF.",
  },
  {
    n: "04",
    title: "Send the packet",
    body: "Print the schedule and worksheet, push the order onward.",
  },
]

export default function Home() {
  return (
    <>
      <Head title="Takeoff and bidding for production flooring">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="FloorCore Measure" />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/icon.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>

      <div className="min-h-screen bg-page text-ink-body">
        <Header />
        <main>
          <Hero />
          <Capabilities />
          <What />
          <How />
          <Closer />
        </main>
        <Footer />
      </div>
    </>
  )
}

function Header() {
  return (
    <header className="flex h-16 items-center justify-between border-b border-hairline px-5 sm:px-8">
      <Link href="/" className="no-underline" aria-label="FloorCore Measure">
        <Logo />
      </Link>
      <nav className="flex items-center gap-4 text-sm sm:gap-6">
        <a href="#what" className="hidden text-ink-body sm:inline">
          What it does
        </a>
        <a href="#how" className="hidden text-ink-body sm:inline">
          How it works
        </a>
        <Link href="/login" className="font-semibold text-ink-body">
          Log in
        </Link>
        <Button asChild size="sm">
          <Link href="/request-access">Request access</Link>
        </Button>
      </nav>
    </header>
  )
}

function Hero() {
  return (
    <section className="bg-brand-navy px-5 py-14 sm:px-8 sm:py-16">
      <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-[1.05fr_1fr]">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-green-light">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-green" />
            Floor Authority · internal tool
          </span>
          <h1 className="display-hero mt-5 text-white">
            Measure the plan once.
            <br />
            Bid the whole community.
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/70">
            {DESCRIPTION}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Button asChild size="lg">
              <Link href="/request-access">Request access</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="ghost"
              className="border border-white/25 text-white hover:bg-white/10 hover:text-white"
            >
              <Link href="/login">Log in</Link>
            </Button>
          </div>
          <p className="mt-4 text-sm text-white/50">
            Accounts are created by invite. If you&apos;re on the estimating team
            and don&apos;t have a login, ask an admin to send one.
          </p>
        </div>

        <div className="overflow-hidden rounded-xl border border-white/15 bg-brand-navy-deep">
          <img
            src="/landing-takeoff-editor.webp"
            alt="The FloorCore Measure editor: a traced floor plan with colour-coded rooms beside the product list for that takeoff."
            width={1036}
            height={849}
            className="block h-[300px] w-full object-cover object-left-top sm:h-[360px]"
          />
        </div>
      </div>
    </section>
  )
}

function Capabilities() {
  return (
    <section className="border-t border-white/10 bg-brand-navy-deep px-5 py-4 sm:px-8">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-2">
        <span className="text-xs font-bold uppercase tracking-wider text-white/50">
          Built for production takeoff
        </span>
        <span className="font-mono text-xs text-white/70">
          PDF plan sets · Room tracing &amp; seams · Level-based bids · Cut sheets
        </span>
      </div>
    </section>
  )
}

function What() {
  return (
    <section id="what" className="px-5 py-16 sm:px-8">
      <div className="mx-auto max-w-6xl">
        <h6>What it does</h6>
        <h2 className="mt-2">Everything between the plan set and the order</h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-lg border border-hairline bg-page p-5"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-faded text-accent-display">
                <Icon className="h-[18px] w-[18px]" />
              </span>
              <h4 className="mt-3.5">{title}</h4>
              <p className="mt-1.5 text-sm leading-relaxed text-ink-muted">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function How() {
  return (
    <section id="how" className="px-5 pb-16 sm:px-8">
      <div className="mx-auto max-w-6xl rounded-xl border border-hairline bg-surface p-7 sm:p-8">
        <h6>How it works</h6>
        <div className="mt-5 grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map(({ n, title, body }) => (
            <div key={n} className="rounded-lg border border-hairline bg-page p-4">
              <span className="font-mono text-xs font-semibold text-accent">{n}</span>
              <h5 className="mt-2">{title}</h5>
              <p className="mt-1 text-[13px] leading-relaxed text-ink-muted">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Closer() {
  return (
    <section className="px-5 pb-16 sm:px-8">
      <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 rounded-xl border border-hairline bg-accent-faded p-7 sm:flex-row sm:items-center sm:p-8">
        <div>
          <h4>Need a login?</h4>
          <p className="mt-1.5 max-w-2xl text-sm text-ink-body">
            Admins create accounts from Admin → Users. You&apos;ll get an email
            with a link to set your password.
          </p>
        </div>
        <Button asChild size="lg" className="shrink-0">
          <Link href="/request-access">Request access</Link>
        </Button>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className="flex flex-col items-center justify-between gap-4 border-t border-hairline px-5 py-5 text-sm text-ink-muted sm:flex-row sm:px-8">
      <span className="flex flex-col items-center gap-3 sm:flex-row">
        {/* The company mark, not the product mark — Measure is a Floor
            Authority tool and the footer is where that attribution belongs. */}
        <img
          src="/floor-authority-logo-navy.svg"
          alt="Floor Authority"
          className="h-6 w-auto dark:hidden"
        />
        <img
          src="/floor-authority-logo-white.svg"
          alt="Floor Authority"
          className="hidden h-6 w-auto dark:block"
        />
        <span>© 2026 Floor Authority Inc. · Myrtle Beach, SC</span>
      </span>
      <a href="mailto:support@floorauthority.com" className="text-ink-muted">
        Support
      </a>
    </footer>
  )
}
